<template>
  <div class="question-list-editor">
    <draggable
      v-model="questionList"
      tag="div"
      class="question-list"
      handle=".item-btn-group .item-btn.drag-btn"
      animation="200"
      ghostClass="ghost"
      :disabled="disabled"
      @change="handleDraggableChange"
    >
      <div
        v-for="(item, index) in questionList"
        class="list-item"
        :key="item.id"
      >
        <div class="item-title">
          <!-- <div class="item-icon">
            <span class="icon" :class="getQuestionIconClass(item)"></span>
          </div> -->
          <div class="item-name">{{ item.title }}</div>
          <div v-if="!disabled" class="item-btn-group">
            <el-dropdown v-if="hasVisibilityOption" class="item-btn visibility-btn" trigger="click" @command="handleVisibleChange(item, $event)">
              <div class="btn-inner-container">
                <span class="btn-label">{{ $t(`${i18nPrefix}.visibilityType.${getVisibilityTypeName(item.visibility)}`) }}</span>
                <i class="btn-icon el-icon-arrow-down el-icon--right"></i>
              </div>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-for="(option, index) in visibilityOptionList" :key="index" :command="option.value">{{ option.label }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <div class="item-btn drag-btn">
              <i class="iconfont iconMore"></i>
            </div>
            <div class="item-btn edit-btn" @click="editQuestion($event, item, index)">
              <i class="el-icon-edit-outline"></i>
            </div>
            <div class="item-btn delete-btn" @click="deleteQuestion($event, item, index)">
              <i class="iconfont iconDelete"></i>
            </div>
          </div>
        </div>
        <div v-if="HAS_OPTIONS_QUESTION_TYPES.includes(item.type)" class="item-options-container">
          <ul class="option-list">
            <li
              v-for="(optionItem, index) in item.options"
              class="option-item"
              :key="index"
              :class="{ knockout: optionItem.is_knockout }"
            >
              <span>{{ optionItem.description }}</span>
            </li>
          </ul>
        </div>
      </div>
    </draggable>
    <div class="add-btn-group">
      <el-button
        v-for="(item, index) in addBtnList"
        v-show="item.show"
        class="add-btn"
        type="text"
        :disabled="disabled"
        :key="index"
        :class="item.class"
        @click="handleAddBtnClick($event, item)"
      >
        {{ item.label }}
      </el-button>
    </div>
    <template v-if="newDialogStyle">
      <DialogWrapper
        close-on-cancel
        destroy-on-close
        class="selector-dialog group-selector"
        :show.sync="groupSelector.show"
        :close-on-click-modal="false"
        :append-to-body="appendDialogToBody"
        :title="$t(`${i18nPrefix}.editor.question.title.${groupSelector.isEdit ? 'edit' : 'add'}`)"
        @save="handleSelectedQuestion"
      >
        <GroupQuestionSelector
          ref="groupQuestionSelector"
          v-model="groupSelector.selected"
          :disabled-question-type="disabledQuestionType"
          :data="groupList"
        />
      </DialogWrapper>
      <DialogWrapper
        close-on-cancel
        class="editor-dialog question-editor"
        :show.sync="questionEditor.show"
        :close-on-click-modal="false"
        :append-to-body="appendDialogToBody"
        :title="$t(`${i18nPrefix}.editor.question.title.${questionEditor.isEdit ? 'edit' : 'add'}`)"
        @save="handleSaveQuestion"
      >
        <QuestionEditor
          ref="questionEditor"
          :has-visibility-option="hasVisibilityOption"
          :default-visibility="defaultVisibility"
          :disabled-question-type="disabledAndSizeLimitQuestionType"
          :is-edit="questionEditor.isEdit"
          :question="questionEditor.question"
        />
      </DialogWrapper>
    </template>
    <template v-else>
      <el-dialog
        class="selector-dialog group-selector"
        destroy-on-close
        :visible.sync="groupSelector.show"
        :close-on-click-modal="false"
        :append-to-body="appendDialogToBody"
        :title="$t(`${i18nPrefix}.editor.question.title.${groupSelector.isEdit ? 'edit' : 'add'}`)"
      >
        <template #default>
          <GroupQuestionSelector
            ref="groupQuestionSelector"
            v-model="groupSelector.selected"
            :disabled-question-type="disabledQuestionType"
            :data="groupList"
          />
        </template>
        <template #footer>
          <div class="editor-footer adapt-wrapper">
            <el-button class="cancel-btn grey-btn" @click="groupSelector.show = false">{{ $t('common.btn.cancel') }}</el-button>
            <el-button class="save-btn blue-btn" @click="handleSelectedQuestion">{{ $t('common.btn.confirm') }}</el-button>
          </div>
        </template>
      </el-dialog>
      <el-dialog
        class="editor-dialog question-editor"
        :visible.sync="questionEditor.show"
        :close-on-click-modal="false"
        :append-to-body="appendDialogToBody"
        :title="$t(`${i18nPrefix}.editor.question.title.${questionEditor.isEdit ? 'edit' : 'add'}`)"
      >
        <template #default>
          <QuestionEditor
            ref="questionEditor"
            :has-visibility-option="hasVisibilityOption"
            :default-visibility="defaultVisibility"
            :disabled-question-type="disabledAndSizeLimitQuestionType"
            :is-edit="questionEditor.isEdit"
            :question="questionEditor.question"
          />
        </template>
        <template #footer>
          <div class="editor-footer adapt-wrapper">
            <el-button class="cancel-btn grey-btn" @click="questionEditor.show = false">{{ $t('common.btn.cancel') }}</el-button>
            <el-button class="save-btn blue-btn" @click="handleSaveQuestion">{{ $t('common.btn.confirm') }}</el-button>
          </div>
        </template>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import draggable from 'vuedraggable'
import { QUESTION_TYPE, HAS_OPTIONS_QUESTION_TYPES, VISIBILITY_TYPE } from '@/constant/openQuestion'
import QuestionEditor, { visibilityOptionList } from './questionEditor.vue'
import GroupQuestionSelector from './groupQuestionSelector.vue'
import { i18nPrefix } from './common'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

export default {
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    groupList: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledQuestionType: {
      type: Array,
      default: () => []
    },
    hasVisibilityOption: {
      type: Boolean,
      default: false
    },
    defaultVisibility: {
      type: String,
      default: VISIBILITY_TYPE.IE
    },
    sizeLimit: {
      type: Object,
      default: () => {
        const limit = {}
        _.forEach(QUESTION_TYPE, (type) => {
          limit[type] = -1
        })
        return limit
      }
    },
    appendDialogToBody: {
      type: Boolean,
      default: false
    },
    disableQuestionGroup: {
      type: Boolean,
      default: false
    },
    newDialogStyle: {
      type: Boolean,
      default: false
    },
    hiddenQuestionTemplate: {
      default: false
    }
  },
  components: {
    draggable,
    GroupQuestionSelector,
    QuestionEditor,
    DialogWrapper
  },
  data () {
    const questionTypeCount = {}
    _.forEach(QUESTION_TYPE, (type) => {
      questionTypeCount[type] = 0
    })
    return {
      i18nPrefix,
      QUESTION_TYPE,
      HAS_OPTIONS_QUESTION_TYPES,
      visibilityOptionList,
      questionList: [],
      questionTypeCount,
      groupSelector: {
        show: false,
        selected: []
      },
      questionEditor: {
        show: false,
        isEdit: false,
        question: null
      }
    }
  },
  computed: {
    disabledAndSizeLimitQuestionType () {
      const types = this.disabledQuestionType
      const typeCount = this.questionTypeCount
      _.forEach(this.sizeLimit, (limit, questionType) => {
        const count = typeCount[questionType]
        if (limit > -1 && count >= limit) {
          types.push(questionType)
        }
      })
      return types
    },
    addBtnList () {
      const btnList = [
        {
          show: true,
          addFromTemplate: false,
          label: this.$t(`${this.i18nPrefix}.btn.addQuestion`)
        },
        {
          show: !this.hiddenQuestionTemplate,
          addFromTemplate: true,
          label: this.$t(`${this.i18nPrefix}.btn.addQuestionFromTemplate`)
        }
      ]
      return btnList
    }
  },
  created () {

  },
  methods: {
    getVisibilityTypeName (visibility) {
      return _.findKey(VISIBILITY_TYPE, (value) => value === visibility)
    },
    getQuestionIconClass (questionItem) {
      const questionType = questionItem.type
      let iconClass = ''
      switch (questionType) {
        case QUESTION_TYPE.TEXT:
          iconClass = 'text'
          break
        case QUESTION_TYPE.SELECT:
          iconClass = 'select'
          break
        case QUESTION_TYPE.CHECKBOX:
          iconClass = 'checkbox'
          break
        case QUESTION_TYPE.ATTACHMENT:
          iconClass = 'attachment'
          break
        case QUESTION_TYPE.VIDEO:
          iconClass = 'video'
          break
      }
      return `${iconClass}-icon`
    },
    checkQuestionTypeLimit (type, count) {
      const limit = this.sizeLimit[type]
      if (limit > -1 && count > limit) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.exceedTheQuestionTypeSizeLimit`, { limit }))
        return true
      }
      return false
    },
    handleDraggableChange () {
      this.$emit('change', this.questionList)
    },
    handleVisibleChange (questionItem, visibility) {
      questionItem.visibility = visibility
    },
    editQuestion ($event, questionItem, index) {
      this.questionEditor.isEdit = true
      this.questionEditor.question = questionItem
      this.questionEditor.show = true
    },
    deleteQuestion ($event, questionItem, index) {
      this.questionTypeCount[questionItem.type] -= 1
      this.questionList.splice(index, 1)
    },
    handleSelectedQuestion () {
      const selected = this.groupSelector.selected
      const questionTypeCount = { ...this.questionTypeCount }
      const newQuestionList = []
      let hasError = false
      let hasDuplicate = false
      _.forEach(selected, question => {
        const count = questionTypeCount[question.type] + 1
        hasError = this.checkQuestionTypeLimit(question.type, count)
        if (hasError) {
          return false
        }
        const newQuestion = {
          id: null,
          type: question.type,
          title: question.title,
          options: _.map(question.options, (option, index) => {
            return {
              description: option.description,
              is_knockout: option.is_knockout,
              order: option.order || index + 1
            }
          }),
          duration: question.duration
        }
        if (this.hasVisibilityOption) {
          newQuestion.visibility = this.defaultVisibility
        }
        if (this.checkQuestionDuplicate(this.questionList, newQuestion)) {
          hasDuplicate = true
        } else {
          newQuestionList.push(newQuestion)
          questionTypeCount[question.type] = count
        }
      })
      if (hasDuplicate) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.duplicateTitle`))
      }
      if (!hasError) {
        this.questionTypeCount = questionTypeCount
        this.questionList.push(...newQuestionList)
        this.groupSelector.show = false
      }
    },
    async handleSaveQuestion () {
      const questionEditorRef = this.$refs.questionEditor
      const questionData = await questionEditorRef.submit()
      if (!questionData) {
        return
      }
      const questionType = questionData.type
      const count = this.questionTypeCount[questionType] + 1
      const hasError = this.checkQuestionTypeLimit(questionType, count)
      if (hasError) {
        return
      }
      if (this.questionEditor.isEdit) {
        const question = this.questionEditor.question
        if (question.type !== questionType) {
          this.questionTypeCount[question.type] -= 1
          this.questionTypeCount[questionType] += 1
        }
        _.assign(question, questionData)
      } else {
        if (this.checkQuestionDuplicate(this.questionList, questionData)) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.duplicateTitle`))
          return
        }
        this.questionTypeCount[questionType] += 1
        this.questionList.push(questionData)
      }
      this.questionEditor.show = false
    },
    handleAddBtnClick ($event, btnItem) {
      if (!btnItem.show) {
        return
      }
      if (btnItem.addFromTemplate) {
        this.groupSelector.selected = []
        this.groupSelector.show = true
        // 每次选择问题的时候需要刷新一下问题，不然问题可能会显示上一个的问题（出现的原因是因为element的dialog并没有完全销毁导致的）
        if (this.$refs.groupQuestionSelector) {
          this.$refs.groupQuestionSelector.refreshGroupList()
        }
      } else {
        this.questionEditor.isEdit = false
        this.questionEditor.question = null
        if (this.$refs.questionEditor) {
          this.$refs.questionEditor.clearData()
        }
        this.questionEditor.show = true
      }
    },
    checkQuestionDuplicate (questionList, questionData) {
      return _.find(questionList, (item) => {
        return item.type === questionData.type && item.title === questionData.title
      })
    }
  },
  watch: {
    value: {
      immediate: true,
      handler () {
        if (this.value) {
          this.questionList = this.value
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.question-list-editor {
  .question-list {
    margin-top: 20px;
    margin-bottom: 40px;

    .list-item {
      padding: 5px 10px;
      border: 1px solid #e7e7e7;
      background: #f1f1f1;

      &:not(:first-child) {
        margin-top: 10px;
      }

      &.ghost {
        opacity: 0.5;
        background-color: #cecece;
      }

      .item-title {
        display: flex;
        line-height: 16px;

        .item-icon {
          line-height: 20px;

          .icon {
            display: inline-block;
            width: 14px;
            height: 14px;
            background-repeat: no-repeat;
            background-size: contain;
          }

          .text-icon {
            background-image: url(~@/assets/image/openQuestion/text.png);
          }

          .select-icon {
            background-image: url(~@/assets/image/openQuestion/select.png);
          }

          .checkbox-icon {
            background-image: url(~@/assets/image/openQuestion/checkbox.png);
          }

          .attachment-icon {
            background-image: url(~@/assets/image/openQuestion/attachment.png);
          }

          .video-icon {
            background-image: url(~@/assets/image/openQuestion/video.png);
          }
        }

        .item-name {
          flex: 1;
          margin-left: 10px;
          color: #3b3b3b;
        }

        .item-btn-group {
          display: flex;
          color: #0096cc;
          align-items: center;

          .item-btn {
            cursor: pointer;

            i {
              font-size: 18px;
            }

            &:not(:first-child) {
              margin-left: 10px;
            }
          }

          .drag-btn {
            cursor: grab;
          }

          .visibility-btn {
            color: inherit;
            font-size: 12px;

            .btn-inner-container {
              .btn-label {
                vertical-align: middle;
              }

              .btn-icon {
                font-size: 14px;
                vertical-align: middle;
              }
            }
          }
        }
      }
      .item-options-container {
        color: #676767;

        .option-list {
          padding-left: 15px;

          .option-item {
            padding: 3px 0;

            &::before {
              content: '-';
              margin-right: 10px;
            }

            &.knockout {
              color: #F56C6C;
            }

            .knockout-tag {
              height: auto;
              margin: 0 10px;
              padding: 1px 5px;
              border-radius: 15px;
              line-height: normal;
            }
          }
        }
      }
    }
  }

  .add-btn-group {
    .add-btn {
      display: inline-block;
      width: 100%;
      margin: 15px auto 0;
      padding: 8px 0;
      border: 1px dashed #8a8a8a;
      border-radius: 0;
      font-size: 12px;

      &:first-child {
        margin-top: 0;
      }
    }
  }

  .selector-dialog {
    &.group-selector{
      .group-question-selector {
        ::v-deep {
          .table-container {
            max-height: 300px;
            overflow: auto;
          }
        }
      }
    }
  }

  .selector-dialog, .editor-dialog {
    .editor-footer {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
